import { defineStore } from 'pinia';
import { computed, reactive, ref } from 'vue';

export type Theme = 'light' | 'dark';

interface InstanceConfiguration {
    appTargetSelector?: string;
    theme: Theme;
    walletConfirmationRequired: boolean;
    hideFooter?: boolean;
    returnUrl?: string;
    attemptDirectConfirm?: boolean;
    sessionIdentifier?: string;
}

const initialInstanceConfiguration: InstanceConfiguration = {
    appTargetSelector: '.btcdirect-widget',
    theme: 'light',
    walletConfirmationRequired: false,
    hideFooter: false,
    returnUrl: null,
    attemptDirectConfirm: false,
    sessionIdentifier: null
};

interface OrderConfiguration {
    callbackUrlC2f: string;
    callbackUrlF2c: string;
}

export const useConfigurationStore = defineStore('configuration', () => {
    const instanceConfiguration = reactive<InstanceConfiguration>({ ...initialInstanceConfiguration });

    const orderConfiguration = reactive<OrderConfiguration>({
        callbackUrlC2f: null,
        callbackUrlF2c: null
    });

    const isUnifiedCheckout = ref<boolean>(false);

    function setInstanceConfiguration(configuration: InstanceConfiguration): void {
        const config = Object.assign({}, initialInstanceConfiguration, configuration);

        instanceConfiguration.appTargetSelector = config.appTargetSelector;
        instanceConfiguration.theme = config.theme;
        instanceConfiguration.walletConfirmationRequired = config.walletConfirmationRequired;
        instanceConfiguration.hideFooter = config.hideFooter;
        instanceConfiguration.returnUrl = config.returnUrl;
        instanceConfiguration.attemptDirectConfirm = config.attemptDirectConfirm;
        instanceConfiguration.sessionIdentifier = crypto.randomUUID();
    }

    function setOrderConfiguration(configuration: OrderConfiguration): void {
        if (!configuration) {
            orderConfiguration.callbackUrlC2f = null;
            orderConfiguration.callbackUrlF2c = null;
            return;
        }

        orderConfiguration.callbackUrlC2f = configuration.callbackUrlC2f ?? null;
        orderConfiguration.callbackUrlF2c = configuration.callbackUrlF2c ?? null;
    }

    function setUnifiedCheckout(value: boolean): void {
        isUnifiedCheckout.value = value === true;
    }

    function setAttemptDirectConfirm(value: boolean): void {
        instanceConfiguration.attemptDirectConfirm = value;
    }

    return {
        appContainerSelector: computed<string>(() => instanceConfiguration.appTargetSelector + ' > div:first-child'),
        instanceConfiguration: computed<InstanceConfiguration>(() => instanceConfiguration),
        orderConfiguration: computed<OrderConfiguration>(() => orderConfiguration),
        mode: computed<string>(() => instanceConfiguration.theme + '-mode'),
        isUnifiedCheckout: computed<boolean>(() => isUnifiedCheckout.value),
        shouldAttemptDirectConfirm: computed<boolean>(() => instanceConfiguration.attemptDirectConfirm),
        setInstanceConfiguration,
        setOrderConfiguration,
        setUnifiedCheckout,
        setAttemptDirectConfirm,
    };
});
